import { useState, useEffect, useRef, useCallback } from "react";
import { Search, ArrowLeft, MapPin } from "lucide-react";
import axios from "axios";
import { API } from "../utils/constants";
import { calcSmartDuration } from "../utils/smartDuration";
import { PlaceCardFull as PlaceCard } from "../components/shared";
import PlaceDetailModal from "../components/PlaceDetailModal";
import BrandHeader from "../components/BrandHeader";

const CatalogPage = ({ onNext, onBack, selectedPlaces, setSelectedPlaces, extractedPlaces, setExtractedPlaces, dates, selectedCities, startOver, curatorBrief }) => {
  const [catalogPlaces, setCatalogPlaces] = useState([]);
  const [activeCity, setActiveCity] = useState("all");
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchFilter, setSearchFilter] = useState("");
  const [showLimit, setShowLimit] = useState(20);
  const [notInTripToast, setNotInTripToast] = useState(null);
  const [detailPlace, setDetailPlace] = useState(null);
  const loaderRef = useRef(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);      // until the places arrive, "loading" — never "0 of 0" (2026-10-26)

  useEffect(() => { fetchCatalog(); }, []);

  const fetchCatalog = async () => {
    setLoadFailed(false);
    try {
      const res = await axios.get(`${API}/catalog/places`);
      setCatalogPlaces(res.data.places);
      setLoaded(true);
    } catch (e) {
      setLoadFailed(true);      // said, never "no places match" (2026-10-07)
    }
  };

  // Only show cities in the user's trip
  const tripCityNames = selectedCities.map(c => c.name || c);
  // All cities from catalog (for filter pills)
  const allCatalogCities = [...new Set(catalogPlaces.map(p => p.city).filter(Boolean))];
  const cities = ["all", ...allCatalogCities];

  const filtered = catalogPlaces.filter(p => {
    // When "all" or a trip city is active, only show trip cities
    if (activeCity === "all") {
      if (tripCityNames.length > 0 && !tripCityNames.includes(p.city)) return false;
    } else {
      if (p.city !== activeCity) return false;
    }
    if (activeCategory !== "all" && p.category !== activeCategory) return false;
    if (searchFilter) {
      // Tokenize query and normalize for singular/plural matching
      const normalize = (s) => (s || "").toLowerCase().replace(/[-_]/g, " ");
      const stem = (w) => w.length > 3 && w.endsWith("s") ? w.slice(0, -1) : w;
      const tokens = normalize(searchFilter).split(/\s+/).filter(Boolean).map(stem);

      // Build a haystack from every searchable field on the card
      const haystack = [
        p.name, p.city, p.neighborhood, p.description, p.title,
        p.description_short, p.description_medium, p.tip, p.insider_note,
        p.what_to_order, p.nearby_food, p.category, p.display_category,
        ...(Array.isArray(p.tags) ? p.tags : []),
        ...(Array.isArray(p.aliases) ? p.aliases : []),
        ...(Array.isArray(p.best_for) ? p.best_for : []),
      ].filter(Boolean).map(normalize).join(" ");
      const haystack_stemmed = haystack.split(/\s+/).map(stem).join(" ");

      // Match if ALL tokens appear somewhere in the haystack
      return tokens.every(t => haystack_stemmed.includes(t));
    }
    return true;
  });

  const hasMore = filtered.length > showLimit;

  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore) setShowLimit(prev => prev + 20);
  }, [hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { root: null, rootMargin: "200px", threshold: 0 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const CAT_FILTER = [
    { key: "all", label: "All categories" },
    { key: "culture", label: "CULTURE", css: "culture" },
    { key: "food", label: "FOODIE", css: "food" },
    { key: "nightlife", label: "NIGHTLIFE", css: "nightlife" },
    { key: "special", label: "SPECIAL", css: "special" },
    { key: "popular", label: "POPULAR", css: "popular" },
    { key: "hidden_gem", label: "HIDDEN", css: "hidden_gem" },
  ];

  const togglePlace = (place) => {
    const isSelected = selectedPlaces.includes(place.name);
    if (isSelected) {
      setSelectedPlaces(prev => prev.filter(n => n !== place.name));
      setExtractedPlaces(prev => prev.filter(p => p.name !== place.name));
    } else {
      setSelectedPlaces(prev => [...prev, place.name]);
      if (!extractedPlaces.find(p => p.name === place.name)) {
        setExtractedPlaces(prev => [...prev, place]);
      }
    }
  };

  // Per-city time calculations
  const activeList = extractedPlaces.filter(p => selectedPlaces.includes(p.name));
  const arrH = parseInt(dates?.arrivalTime?.split(":")[0] || "14");
  const depH = parseInt(dates?.departureTime?.split(":")[0] || "18");
  const isLateArr = arrH >= 22 || arrH < 3;
  const sortedCities = (selectedCities || []);
  // Door-to-door travel times (hotel checkout → station → train → station → hotel checkin)
  const TRAVEL_TIMES = {
    "Tokyo-Osaka": 4.5, "Tokyo-Kyoto": 4, "Osaka-Kyoto": 1.5,
    "Tokyo-Hiroshima": 5.5, "Osaka-Hiroshima": 3, "Kyoto-Hiroshima": 3.5,
    "Tokyo-Nara": 4.5, "Osaka-Nara": 1.5, "Kyoto-Nara": 1.5,
    "Tokyo-Hakone": 2.5, "Osaka-Hakone": 4.5,
  };
  const getTravelTime = (a, b) => {
    if (a === b) return 0;
    return TRAVEL_TIMES[`${a}-${b}`] || TRAVEL_TIMES[`${b}-${a}`] || 2;
  };

  // Single source of truth: read from curatorBrief (backend-computed, respects
  // pace + day_start/day_end). Falls back to legacy calculator only if missing.
  const cityTimeData = sortedCities.map((c, idx) => {
    const cityPlaces = activeList.filter(p => p.city === c.name);
    const cityDur = calcSmartDuration(cityPlaces);
    const fromBrief = curatorBrief?.cities?.[c.name]?.available_hours;
    let availH;
    if (fromBrief != null) {
      availH = fromBrief;
    } else {
      // Legacy fallback
      const days = c.days || 1;
      const isFirst = idx === 0;
      const isLast = idx === sortedCities.length - 1;
      availH = days * 12;
      if (isFirst) {
        const readyH = isLateArr ? 9 : Math.min(arrH + 4, 21);
        availH = Math.max(0, 21 - readyH) + (days - 1) * 12;
      }
      if (isLast && !isFirst) {
        const lastH = Math.max(0, Math.max(depH - 4, 6) - 9);
        availH = (days - 1) * 12 + lastH;
      }
      if (isFirst && isLast && days === 1) {
        const startH = isLateArr ? 9 : Math.min(arrH + 4, 21);
        availH = Math.max(0, Math.max(depH - 4, 6) - startH);
      }
      if (idx < sortedCities.length - 1) availH -= getTravelTime(c.name, sortedCities[idx + 1].name);
      availH = Math.max(0, availH);
    }
    const freeH = availH - cityDur.totalNeeded;
    return { name: c.name, count: cityPlaces.length, availH, neededH: cityDur.totalNeeded, freeH, isOver: freeH < -1 };
  });

  const totalFreeH = cityTimeData.reduce((s, c) => s + c.freeH, 0);
  const anyOver = cityTimeData.some(c => c.isOver);

  return (
    <div className="min-h-screen" style={{ background: "var(--zen-bg)" }}>
      <BrandHeader right={
        <button onClick={onBack}
          className="font-mono-label flex items-center gap-1.5 hover:opacity-70 transition-opacity"
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, letterSpacing: "0.16em", color: "var(--zen-sub)" }}
          data-testid="catalog-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      } />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="zen-page-header">
          <h1 className="font-heading">A Curated Japan, by Us for You</h1>
          <p>{catalogPlaces.length > 0 ? `${catalogPlaces.length} curated spots` : "Curated spots"} across Tokyo, Kyoto & Osaka. Pick what calls to you.</p>
        </div>

        {/* Time bar — per-city, updates live */}
        {selectedPlaces.length > 0 && cityTimeData.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="catalog-time-bar">
            {cityTimeData.map(ct => (
              <div key={ct.name} className="rounded-lg p-2.5"
                style={{background: ct.isOver ? 'rgba(220,75,45,0.06)' : 'var(--zen-s1)', border: `1px solid ${ct.isOver ? 'var(--cat-nightlife-solid)' : 'var(--zen-border)'}`}}>
                <div className="flex items-center justify-between text-xs">
                  <span style={{color:'var(--zen-sub)'}}>
                    <strong>{ct.name}</strong> · {ct.count} spots · ~{Math.round(ct.neededH)}h needed
                  </span>
                  <span className="font-medium" style={{color: ct.isOver ? 'var(--cat-nightlife-solid)' : ct.freeH < 3 ? 'var(--cat-culture-solid)' : 'var(--cat-foodie-solid)'}}>
                    {ct.isOver ? `${Math.abs(Math.round(ct.freeH))}h over` : `${Math.round(ct.freeH)}h free`}
                  </span>
                </div>
                <div className="w-full rounded-full h-1 mt-1.5" style={{background:'var(--zen-border-h)'}}>
                  <div className="h-1 rounded-full transition-all" style={{
                    background: ct.isOver ? 'var(--cat-nightlife-solid)' : 'var(--zen-accent)',
                    width: `${Math.min(100, ct.availH > 0 ? Math.round((ct.neededH / ct.availH) * 100) : 0)}%`
                  }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* City filter pills */}
        <div className="zen-filter-bar mb-3" data-testid="city-tabs">
          {cities.map(city => {
            const isInTrip = city === "all" || tripCityNames.includes(city);
            return (
              <button key={city}
                onClick={() => {
                  if (!isInTrip) {
                    setNotInTripToast(city);
                    setTimeout(() => setNotInTripToast(null), 3000);
                    return;
                  }
                  setActiveCity(city);
                  setShowLimit(20);
                }}
                className={`zen-city-pill ${activeCity === city ? 'active' : ''}`}
                style={!isInTrip ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                data-testid={`city-tab-${city}`}>
                {city === "all" ? "All cities" : city}
              </button>
            );
          })}
        </div>
        {notInTripToast && (
          <div className="mb-3 px-4 py-2.5 rounded-xl text-xs font-medium flex items-center gap-2 animate-in fade-in"
            style={{ background: 'var(--zen-s2)', border: '1px solid var(--zen-border)', color: 'var(--zen-sub)' }}
            data-testid="not-in-trip-toast">
            <MapPin className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--zen-accent)' }} />
            <span><strong>{notInTripToast}</strong> is not part of your trip. Go back and add it to your cities to explore its spots.</span>
          </div>
        )}

        {/* Category filter tags */}
        <div className="zen-filter-bar mb-4" data-testid="category-chips">
          {CAT_FILTER.map(cat => (
            <button key={cat.key} onClick={() => { setActiveCategory(cat.key); setShowLimit(20); }}
              className={`zen-cat-tag ${cat.key === 'all' ? 'all-tag' : cat.css} ${activeCategory === cat.key ? 'active' : ''}`}
              data-testid={`cat-chip-${cat.key}`}>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="relative mb-5">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{color:'var(--zen-dim)'}} />
          <input type="text" value={searchFilter} onChange={(e) => { setSearchFilter(e.target.value); setShowLimit(20); }}
            placeholder="Search places, neighborhoods, keywords..."
            className="zen-search" data-testid="catalog-search" />
        </div>

        {/* Results count */}
        <p className="text-xs mb-3" style={{color:'var(--zen-dim)'}}>
          {loaded ? `Showing ${Math.min(showLimit, filtered.length)} of ${filtered.length} spots` : "Loading the places…"}
        </p>

        {/* Card grid */}
        <div className="zen-grid" data-testid="catalog-grid">
          {filtered.slice(0, showLimit).map((place, i) => (
            <PlaceCard key={`${place.name}-${i}`} place={place} selected={selectedPlaces.includes(place.name)}
              onToggle={() => togglePlace(place)}
              onViewDetail={() => setDetailPlace(place)} />
          ))}
        </div>

        {hasMore && (
          <div ref={loaderRef} className="text-center py-8" data-testid="infinite-scroll-loader">
            <span className="text-sm" style={{color:'var(--zen-dim)'}}>Loading more...</span>
          </div>
        )}

        {loadFailed ? (
          <div className="text-center py-16" role="alert">
            <p style={{color:'var(--zen-sub)'}}>We couldn't load the places — check your connection.</p>
            <button onClick={fetchCatalog} className="zen-btn zen-btn-outline mt-4 text-sm" style={{ padding: "10px 18px" }}>Try again</button>
          </div>
        ) : loaded && filtered.length === 0 && (
          <p className="text-center py-16" style={{color:'var(--zen-dim)'}}>No places match your filters.</p>
        )}

        {/* Single sticky CTA at bottom */}
        {selectedPlaces.length > 0 && (
          <div className="sticky bottom-6 flex justify-center pt-8">
            <button onClick={() => onNext()}
              className="zen-btn zen-btn-primary shadow-lg" data-testid="catalog-done-btn">
              <ArrowLeft className="w-4 h-4" /> Back to my trip ({selectedPlaces.length} spots)
            </button>
          </div>
        )}
      </div>
      <PlaceDetailModal
        place={detailPlace}
        isOpen={!!detailPlace}
        onClose={() => setDetailPlace(null)}
        isSelected={detailPlace ? selectedPlaces.includes(detailPlace.name) : false}
        onToggle={() => { if (detailPlace) togglePlace(detailPlace); }}
      />
    </div>
  );
};

export default CatalogPage;
